import { useCallback, useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'
import { useVerifyEmail, useResendVerification } from '@/hooks/useAuth'
import { ApiError } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { Footer } from '@/components/Footer'
import { cn } from '@/lib/utils'
import { createLogger } from '@/utils/logger'
import { tokenFromLocation } from '@/lib/linkToken'

const logger = createLogger('VerifyEmail')

/**
 * Landing page for the verification link in the email .
 *
 * The link itself is a side-effect-free GET to this page; the actual state
 * change happens here via a POST (so an email scanner pre-fetching the link
 * doesn't consume the token). On success the backend issues a session and sets
 * the same cookies as login, so we persist it through the SAME auth-store path
 * as login (via useVerifyEmail) and land the user in the app.
 *
 * Four states, the resilience contract for any async surface:
 *  - LOADING  → spinner while the token is in flight
 *  - DATA     → success: redirect into the app (no idle screen)
 *  - ERROR    → 410 invalid/expired link (resend) OR 503 unavailable (retry)
 *  - EMPTY    → no `?token=` at all → treated as an invalid link
 */
// The four mutually-exclusive screens. Success is NOT one of them — a verified
// token logs the user in and navigates away, so there is no idle success state.
type VerifyState = 'verifying' | 'invalid' | 'unavailable'

// Single-flight guard for the token, keyed by its raw value. The token is
// single-use, so the POST must fire exactly once per outcome: a second POST hits
// a consumed token and gets a 410 that clobbers the success. Checked and set
// synchronously before the first `await` in runVerify, so it dedupes a
// same-instance double-invoke (StrictMode in dev, an effect re-run) as well.
//
// It is also the 503 retry handler: a FAILED attempt removes itself so the
// "unavailable" screen's retry button can re-POST the same token. A token that
// verified successfully stays in the set permanently — it is spent, and the page
// is navigating away for good.
//
// Historical note, because the shape of this guard looks over-cautious without
// it: this used to be the fix for a REMOUNT. <AuthIdentityBoundary> re-keyed its
// subtree on every identity change, sign-in included, so a successful verify
// replaced this page mid-flight with a fresh instance whose `useRef` had reset.
// That transition is now excluded at the boundary (see AuthIdentityBoundary.tsx,
// which carries the full rationale); this guard remains for the same-instance
// cases above, which the boundary has nothing to do with.
const verifiedOrInFlight = new Set<string>()

export default function VerifyEmail() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const token = tokenFromLocation()

  const verify = useVerifyEmail()
  const resend = useResendVerification()
  const [resendEmail, setResendEmail] = useState('')

  // This page owns its render state locally rather than reading the mutation's
  // render flags. The token is single-use, so the POST must fire exactly once
  // per outcome, and a naive `isIdle` guard fires twice — a late second 410 on
  // the consumed token clobbers the success.
  // Initial screen: verifying (token present) or invalid (no `?token=` at all
  // → a dead link).
  const [state, setState] = useState<VerifyState>(token === '' ? 'invalid' : 'verifying')

  // Map a verification failure to a screen. 503 is a retryable backend fault
  // (consume/load failed) — distinct from a genuinely dead link; everything else
  // (410 bad/expired/consumed) is recovered via resend.
  const failureState = (err: unknown): VerifyState =>
    err instanceof ApiError && err.status === 503 ? 'unavailable' : 'invalid'

  // runVerify is the single place that POSTs the token; verifiedOrInFlight (see
  // above) is its guard.
  const { mutateAsync: verifyAsync } = verify
  const runVerify = useCallback(async () => {
    if (token === '' || verifiedOrInFlight.has(token)) return
    verifiedOrInFlight.add(token)
    setState('verifying')
    try {
      const result = await verifyAsync(token)
      if (result.verified) {
        // Verified + logged in (the hook persisted the session). /home
        // bounces a not-yet-onboarded user on to /onboarding via the route
        // guard — the correct next step.
        void navigate('/home', { replace: true })
      } else {
        // A REPLAY of an already-consumed token (see AlreadyVerifiedResponse)
        // — the account is verified but this response minted no session, by
        // design. Send them to log in normally rather than stranding them on
        // this page or silently doing nothing.
        void navigate('/login?notice=already_verified', { replace: true })
      }
      // Leave the token in verifiedOrInFlight either way: it's single-use
      // (or already known-replayed) and we're navigating away for good.
    } catch (err) {
      logger.error('Email verification failed', err)
      verifiedOrInFlight.delete(token)
      setState(failureState(err))
    }
  }, [token, verifyAsync, navigate])

  useEffect(() => {
    void runVerify()
  }, [runVerify])

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col">
      <div className="flex-1 flex flex-col justify-center px-6 py-12 relative">
        <div className="text-center">
          {/* LOADING — token in flight. */}
          {state === 'verifying' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-primary/10 flex items-center justify-center">
                <Spinner className="h-8 w-8 text-primary" />
              </div>
              <h1 className="text-2xl font-display font-semibold text-neutral-900 tracking-tight">
                {t('auth.verify.pending.title')}
              </h1>
              <p className="mt-3 text-sm text-neutral-500">
                {t('auth.verify.pending.description')}
              </p>
            </>
          )}

          {/* ERROR (503) — backend fault, retryable with the same token. */}
          {state === 'unavailable' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-error/10 flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-error" aria-hidden="true" />
              </div>
              <h1 className="text-2xl font-display font-semibold text-neutral-900 tracking-tight">
                {t('auth.verify.unavailable.title')}
              </h1>
              <p className="mt-3 text-sm text-neutral-500">
                {t('auth.verify.unavailable.description')}
              </p>
              <Button
                type="button"
                onClick={() => void runVerify()}
                disabled={verify.isPending}
                className={cn(
                  "w-full h-12 text-base font-medium mt-8",
                  "bg-primary hover:bg-primary-600 active:bg-primary-700",
                  "transition-colors duration-150",
                  "disabled:opacity-50"
                )}
              >
                {t('auth.verify.unavailable.retry')}
              </Button>
            </>
          )}

          {/* ERROR (410 / missing token) — dead link, recover via resend. */}
          {state === 'invalid' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-error/10 flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-error" aria-hidden="true" />
              </div>
              <h1 className="text-2xl font-display font-semibold text-neutral-900 tracking-tight">
                {t('auth.verify.invalid.title')}
              </h1>
              <p className="mt-3 text-sm text-neutral-500 leading-relaxed">
                {t('auth.verify.invalid.description')}
              </p>

              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  resend.mutate(resendEmail)
                }}
                className="mt-8 space-y-3 text-left"
              >
                <label htmlFor="resend-email" className="text-sm font-medium text-neutral-700">
                  {t('auth.email')}
                </label>
                <Input
                  id="resend-email"
                  type="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder={t('auth.emailPlaceholder', 'name@hospital.ch')}
                  className={cn(
                    "h-12 text-base bg-white border-neutral-200",
                    "focus:border-primary focus:ring-primary/20",
                    "placeholder:text-neutral-400"
                  )}
                />
                <Button
                  type="submit"
                  disabled={resend.isPending || !resendEmail.includes('@')}
                  className={cn(
                    "w-full h-12 text-base font-medium",
                    "bg-primary hover:bg-primary-600 active:bg-primary-700",
                    "transition-colors duration-150",
                    "disabled:opacity-50"
                  )}
                >
                  {resend.isPending ? (
                    <span className="flex items-center gap-2">
                      <Spinner className="h-4 w-4" />
                      {t('common.loading')}
                    </span>
                  ) : (
                    t('auth.verify.invalid.resend')
                  )}
                </Button>
                {resend.isSuccess && !resend.isPending && (
                  <p className="text-sm text-primary" role="status">
                    {t('auth.verify.invalid.resent')}
                  </p>
                )}
                {resend.isError && !resend.isPending && (
                  <p className="text-sm text-error" role="alert">
                    {t('auth.verify.resendFailed')}
                  </p>
                )}
              </form>

              <p className="mt-8 text-center text-sm text-neutral-500">
                <Link
                  to="/login"
                  className="font-medium text-primary hover:text-primary-600 transition-colors"
                >
                  {t('auth.verify.invalid.backToLogin')}
                </Link>
              </p>
            </>
          )}
        </div>
      </div>

      <Footer />
      <div className="h-safe-area-inset-bottom" />
    </div>
  )
}

